import React, { useEffect, useRef, useState } from "react";
import "./VisitorMap.css";

const NUMBER_FORMATTER = new Intl.NumberFormat();
const HISTORICAL_COUNTRIES = {
  US: 4167,
  CN: 1366,
  CA: 130,
  IN: 123,
  GB: 332,
  DE: 320,
  FR: 242,
  NL: 94,
  SE: 67,
  CH: 59,
  PL: 39,
  BE: 34,
  IE: 31,
  AT: 28,
  DK: 16,
  NO: 14,
  FI: 12,
  PT: 12,
  CZ: 11,
  RO: 11,
  GR: 6,
  HU: 6,
};
const HISTORICAL_VISITS = Object.values(HISTORICAL_COUNTRIES).reduce(
  (total, visits) => total + visits,
  0
);

function mergeCountryCounts(trackedCountries) {
  const values = { ...HISTORICAL_COUNTRIES };
  Object.entries(trackedCountries || {}).forEach(([code, count]) => {
    const amount = Number(count) || 0;
    if (!amount) {
      return;
    }
    values[code] = (Number(values[code]) || 0) + amount;
  });
  return values;
}

function hexToRgb(hex) {
  const value = hex.replace("#", "");
  return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16));
}

function interpolateColor(start, end, fraction) {
  const from = hexToRgb(start);
  const to = hexToRgb(end);
  const channels = from.map((channel, index) =>
    Math.round(channel + (to[index] - channel) * fraction)
      .toString(16)
      .padStart(2, "0")
  );
  return `#${channels.join("")}`;
}

function formatCaption(total) {
  return `${NUMBER_FORMATTER.format(total)} ${total === 1 ? "visit" : "visits"}`;
}

const VisitorMap = () => {
  const mapElement = useRef(null);
  const [status, setStatus] = useState("loading");
  const [caption, setCaption] = useState("Loading visitor statistics…");

  useEffect(() => {
    const controller = new AbortController();
    let map = null;

    const initializeMap = async () => {
      try {
        if (!window.jsVectorMap) {
          throw new Error("jsVectorMap is unavailable");
        }

        const publicPath = process.env.PUBLIC_URL || "";
        const response = await fetch(`${publicPath}/visitor-locations.json`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Visitor statistics request failed (${response.status})`);
        }

        const data = await response.json();
        const trackedCountries = data && typeof data.countries === "object" ? data.countries : {};
        const values = mergeCountryCounts(trackedCountries);
        const countryCodes = Object.keys(values);
        const tracked = Number.isFinite(Number(data.total)) ? Number(data.total) : 0;
        const total = HISTORICAL_VISITS + tracked;
        const logs = {};

        countryCodes.forEach((code) => {
          logs[code] = Math.log10((Number(values[code]) || 0) + 1);
        });
        const maximumLog = Math.max(1, ...Object.values(logs));

        map = new window.jsVectorMap({
          selector: mapElement.current,
          map: "world",
          zoomButtons: true,
          backgroundColor: "transparent",
          regionStyle: {
            initial: { fill: "#e6ebf0", stroke: "#ffffff", strokeWidth: 0.4 },
            hover: { fill: "#16a085", fillOpacity: 1 },
          },
          onRegionTooltipShow: (event, tooltip, code) => {
            const count = Number(values[code]) || 0;
            tooltip.text(
              `${tooltip.text()}: ${NUMBER_FORMATTER.format(count)} ${count === 1 ? "visit" : "visits"}`,
              true
            );
          },
        });

        countryCodes.forEach((code) => {
          const region = map.regions[code];
          if (region && region.element) {
            region.element.setStyle(
              "fill",
              interpolateColor("#cfe8fb", "#0b4975", logs[code] / maximumLog)
            );
          }
        });

        setCaption(formatCaption(total));
        setStatus("ready");
      } catch (error) {
        if (error.name !== "AbortError") {
          setCaption("Visitor statistics are temporarily unavailable.");
          setStatus("error");
        }
      }
    };

    initializeMap();

    return () => {
      controller.abort();
      if (map && typeof map.destroy === "function") {
        map.destroy();
      }
    };
  }, []);

  return (
    <section className="visitor-map-section" aria-labelledby="visitor-map-heading">
      <h2 id="visitor-map-heading" className="visitor-map-heading">Visitors</h2>
      <div
        ref={mapElement}
        className={`visitor-map ${status === "error" ? "visitor-map--hidden" : ""}`}
        role="img"
        aria-label="World map shaded by visitor count per country"
      />
      {caption && <p className="visitor-map-caption" aria-live="polite">{caption}</p>}
    </section>
  );
};

export default VisitorMap;
